import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// After a successful save, let the live-refresh poller look again soon (lib/liveSync.js) so every open screen catches up.
api.interceptors.response.use((r) => {
  if (/^(post|put|patch|delete)$/i.test(r.config?.method || '') && typeof window !== 'undefined') window.dispatchEvent(new Event('sensoper:saved'));
  return r;
});

export const syncAPI = { version: () => api.get('/sync/version') };

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  login2fa: (code) => api.post('/auth/login/2fa', { code }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  twoFaStatus: () => api.get('/auth/2fa/status'),
  twoFaSetup: () => api.post('/auth/2fa/setup'),
  twoFaEnable: (code) => api.post('/auth/2fa/enable', { code }),
  twoFaDisable: (password, code) => api.post('/auth/2fa/disable', { password, code })
};

// Iter 53 — admin credentials management
export const securityAPI = {
  credentials: () => api.get('/security/credentials'),
  updateConfig: (data) => api.put('/security/config', data),
  requireReset: (userId) => api.post(`/security/credentials/${userId}/require-reset`),
  adminDisable2fa: (userId) => api.post(`/security/credentials/${userId}/disable-2fa`)
};

// Iter 53 — unified approvals inbox
export const inboxAPI = {
  list: (source) => api.get('/approvals/inbox', { params: source && source !== 'all' ? { source } : {} }),
  count: () => api.get('/approvals/inbox/count'),
  history: () => api.get('/approvals/inbox/history'),
  approve: (source, id) => api.post(`/approvals/inbox/${source}/${id}/approve`, {}),
  reject: (source, id, reason) => api.post(`/approvals/inbox/${source}/${id}/reject`, { reason })
};

// Projects API
export const projectsAPI = {
  getAll: (status = null) => api.get('/projects', { params: status ? { status } : {} }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  submit: (id) => api.post(`/projects/${id}/submit`),
  approve: (id) => api.post(`/projects/${id}/approve`),
  reject: (id, reason) => api.post(`/projects/${id}/reject`, { reason }),
  complete: (id, payload) => api.post(`/projects/${id}/complete`, payload),
  requestDeletion: (id, reason) => api.post(`/projects/${id}/request-deletion`, { reason }),
  forceDelete: (id) => api.delete(`/projects/${id}/force`),
  updateReference: (id, refNumber) => api.put(`/projects/${id}/reference`, { reference_number: refNumber }),
  updateStatus: (id, status) => api.put(`/projects/${id}/status`, { status }),
  updateNotes: (id, notes) => api.put(`/projects/${id}`, { notes }),
  appendNote: (id, text) => api.post(`/projects/${id}/notes`, { text }),
  getReferenceCandidates: (q) => api.get('/projects/reference-candidates', { params: q ? { q } : {} }),
  getReferenceSummary: (id) => api.get(`/projects/${id}/reference-summary`),
  backfillLocations: (payload = {}) => api.post('/projects/backfill-locations', payload),
  galleryUrl: (id) => `${API_URL}/api/projects/${id}/gallery`
};

// Users API (Admin only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`)
};

// Pricing API removed - costs come from inventory items

// Dashboard API
export const dashboardAPI = {
  getMonthlyTarget: (params = {}) => api.get('/dashboard/monthly-target', { params }),
  getStats: () => api.get('/dashboard/stats'),
  getCeo: (params = {}) => api.get('/dashboard/ceo', { params })
};

// Reports API
export const reportsAPI = {
  get: (type, params = {}) => api.get(`/reports/${type}`, { params })
};

// Pricelist API (Iter 51)
export const pricelistAPI = {
  list: (params = {}) => api.get('/pricelist', { params }),
  update: (id, data) => api.put(`/pricelist/items/${id}`, data),
  bulk: (data) => api.post('/pricelist/bulk', data),
  history: (id) => api.get(`/pricelist/items/${id}/history`),
  recentHistory: (limit = 50) => api.get('/pricelist/history', { params: { limit } }),
  normalisePreview: () => api.get('/pricelist/normalise-categories'),
  normaliseApply: () => api.post('/pricelist/normalise-categories'),
  setCategory: (id, category) => api.post(`/pricelist/items/${id}/category`, { category })
};

// Daily Updates API
// Daily report (one per person per day) and site diary (one per project per day)
export const dailyReportsAPI = {
  options: () => api.get('/daily-reports/options'),
  projects: () => api.get('/daily-reports/projects'),
  mine: (date) => api.get('/daily-reports/me', { params: { date } }),
  saveMine: (date, data) => api.put('/daily-reports/me', data, { params: { date } }),
  list: (params = {}) => api.get('/daily-reports', { params }),
  team: (date) => api.get('/daily-reports/team', { params: { date } }),
  get: (id) => api.get(`/daily-reports/${id}`),
  review: (id, comment = '') => api.put(`/daily-reports/${id}/review`, { comment }),
  remove: (id) => api.delete(`/daily-reports/${id}`),
};

export const siteDiaryAPI = {
  list: (params = {}) => api.get('/site-diaries', { params }),
  get: (projectId, date) => api.get(`/site-diaries/${projectId}/${date}`),
  save: (projectId, date, data) => api.put(`/site-diaries/${projectId}/${date}`, data),
  book: (projectId, params = {}) => api.get(`/site-diaries/project/${projectId}`, { params }),
  remove: (id) => api.delete(`/site-diaries/${id}`),
};

export const dailyUpdatesAPI = {
  create: (data) => api.post('/daily-updates', data),
  list: (params = {}) => api.get('/daily-updates', { params }),
  getByProject: (projectId) => api.get(`/daily-updates/project/${projectId}`),
  update: (id, data) => api.put(`/daily-updates/${id}`, data),
  delete: (id) => api.delete(`/daily-updates/${id}`)
};

// Payments API
export const paymentsAPI = {
  create: (data) => api.post('/payments', data),
  getByProject: (projectId) => api.get(`/payments/project/${projectId}`)
};

// Material Usage API
export const materialUsageAPI = {
  create: (data) => api.post('/material-usage', data),
  getByProject: (projectId) => api.get(`/material-usage/project/${projectId}`)
};

// Project Report & Completeness
export const projectReportAPI = {
  getReport: (projectId) => api.get(`/projects/${projectId}/report`),
  getCompleteness: (projectId) => api.get(`/projects/${projectId}/completeness`)
};

// Alerts & Risk
export const alertsAPI = {
  getDashboard: () => api.get('/alerts/dashboard'),
  getProjectAlerts: (projectId) => api.get(`/alerts/project/${projectId}`)
};

// Thresholds
export const thresholdsAPI = {
  get: () => api.get('/settings/thresholds'),
  update: (data) => api.put('/settings/thresholds', data)
};

// Catalogue (Iter 44 Phase 1) — product-level pricing across panels / inverters / batteries / pumps / structure / services / fuels
export const catalogueAPI = {
  list: (cat, active_only = false) => api.get(`/catalogue/products/${cat}`, { params: { active_only } }),
  create: (cat, data) => api.post(`/catalogue/products/${cat}`, data),
  update: (cat, pid, data) => api.put(`/catalogue/products/${cat}/${pid}`, data),
  delete: (cat, pid) => api.delete(`/catalogue/products/${cat}/${pid}`),
  history: (cat, pid) => api.get(`/catalogue/products/${cat}/${pid}/history`),
  importCsv: (cat, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/catalogue/products/${cat}/import`, fd, { headers: { 'Content-Type': undefined } }); },
  getConfig: () => api.get('/catalogue/config'),
  updateConfig: (data) => api.put('/catalogue/config', data),
  seed: () => api.post('/catalogue/seed'),
  addonGroups: () => api.get('/catalogue/addon-groups'),
};

// Customer Credits
export const creditsAPI = {
  create: (data) => api.post('/credits', data),
  list: (params = {}) => api.get('/credits', { params }),
  pay: (id, data) => api.post(`/credits/${id}/pay`, data),
  getPayments: (id) => api.get(`/credits/${id}/payments`),
  delete: (id) => api.delete(`/credits/${id}`),
  setRate: (id, monthly_pct) => api.put(`/credits/${id}/interest-rate`, { monthly_pct }),
  customerRates: () => api.get('/credits/customer-rates'),
  setCustomerRate: (customer_name, monthly_pct) => api.put('/credits/customer-rates', { customer_name, monthly_pct })
};

// Iter 54 — geo reference (vendor state/district dropdowns)
export const geoAPI = {
  states: () => api.get('/geo/states'),
  districts: (state) => api.get('/geo/districts', { params: state ? { state } : {} }),
  addDistrict: (state, district) => api.post('/geo/districts', { state, district })
};

// Iter 54 — Credential Vault (admin only)
export const vaultAPI = {
  list: (params = {}) => api.get('/vault', { params }),
  dashboard: () => api.get('/vault/dashboard'),
  create: (data) => api.post('/vault', data),
  update: (id, data) => api.put(`/vault/${id}`, data),
  remove: (id) => api.delete(`/vault/${id}`),
  reveal: (id) => api.post(`/vault/${id}/reveal`),
  accessLog: (id) => api.get(`/vault/${id}/access-log`),
  setConfig: (rotation_days) => api.put('/vault/config', { rotation_days })
};


// Purchase Orders
export const purchaseOrdersAPI = {
  create: (data) => api.post('/purchase-orders', data),
  nextNumber: (location_id) => api.get('/purchase-orders/next-number', { params: location_id ? { location_id } : {} }),
  list: (params = {}) => api.get('/purchase-orders', { params }),
  approve: (id) => api.put(`/purchase-orders/${id}/approve`),
  arrival: (id, data) => api.put(`/purchase-orders/${id}/arrival`, data),
  qc: (id, data) => api.put(`/purchase-orders/${id}/qc`, data),
  inbound: (id, data) => api.put(`/purchase-orders/${id}/inbound`, data),
  editInbound: (id, data) => api.put(`/purchase-orders/${id}/inbound/edit`, data),
  reverseInbound: (id) => api.delete(`/purchase-orders/${id}/inbound`),
  remove: (id) => api.delete(`/purchase-orders/${id}`)
};

// Generic action-request queue (delivery cancel / asset archive / sale cancel — location-scoped)
export const actionRequestsAPI = {
  list: (params = {}) => api.get('/action-requests', { params }),
  approve: (id) => api.post(`/action-requests/${id}/approve`),
  reject: (id) => api.post(`/action-requests/${id}/reject`)
};

// GST Tax Invoice + Profit Calculator (Iter 44 Batch A)
export const invoicingAPI = {
  getSettings: () => api.get('/invoice-settings'),
  updateSettings: (data) => api.put('/invoice-settings', data),
  getInvoice: (projectId) => api.get(`/projects/${projectId}/invoice`),
  generateInvoice: (projectId, data = {}) => api.post(`/projects/${projectId}/invoice`, data),
  getProfit: (projectId) => api.get(`/projects/${projectId}/profit`)
};

// Inbound reversal approval queue (managers without delete rights → admin)
export const inboundApprovalsAPI = {
  list: (status = null) => api.get('/inbound-action-requests', { params: status ? { status } : {} }),
  approve: (id) => api.post(`/inbound-action-requests/${id}/approve`),
  reject: (id) => api.post(`/inbound-action-requests/${id}/reject`)
};

// Deliveries
export const deliveriesAPI = {
  create: (data) => api.post('/deliveries', data),
  list: (params = {}) => api.get('/deliveries', { params }),
  complete: (id) => api.put(`/deliveries/${id}/complete`),
  edit: (id, data) => api.put(`/deliveries/${id}`, data),
  cancel: (id) => api.delete(`/deliveries/${id}`)
};

// Brand Returns
export const returnsAPI = {
  create: (data) => api.post('/returns', data),
  list: (params = {}) => api.get('/returns', { params }),
  complete: (id) => api.put(`/returns/${id}/complete`),
  remove: (id, reason) => api.delete(`/returns/${id}`, { data: { reason } })
};

export const kitCategoriesAPI = {
  list: (params = {}) => api.get('/kit-categories', { params }),
  create: (data) => api.post('/kit-categories', data),
  update: (id, data) => api.put(`/kit-categories/${id}`, data)
};

export const locationReviewAPI = {
  list: () => api.get('/projects/location-review'),
  count: () => api.get('/projects/location-review/count'),
  autoResolve: () => api.post('/projects/location-review/auto-resolve'),
  fix: (id, data) => api.put(`/projects/${id}/location-fix`, data)
};

export const pricingSlabsAPI = {
  list: (params = {}) => api.get('/pricing-slabs', { params }),
  lookup: (category, value, on_date) => api.get('/pricing-slabs/lookup', { params: { category, value, on_date } }),
  create: (data) => api.post('/pricing-slabs', data),
  update: (id, data) => api.put(`/pricing-slabs/${id}`, data),
  remove: (id) => api.delete(`/pricing-slabs/${id}`)
};

// Audits
export const auditsAPI = {
  create: (data) => api.post('/audits', data),
  list: (params = {}) => api.get('/audits', { params }),
  update: (id, data) => api.put(`/audits/${id}`, data),
  addIssue: (id, data) => api.put(`/audits/${id}/issue`, data),
  thisWeek: () => api.post('/audits/this-week'),
  setPointStatus: (id, idx, status) => api.put(`/audits/${id}/issue/${idx}/status`, { status }),
  templates: () => api.get('/audit-templates'),
  saveTemplates: (templates) => api.put('/audit-templates', { templates })
};

// AI API

// Terms & Conditions API
export const termsAPI = {
  getAll: (category) => api.get('/terms', { params: category ? { category } : {} }),
  getActive: (language = 'en', category = 'quotation') => api.get('/terms/active', { params: { language, category } }),
  getById: (id) => api.get(`/terms/${id}`),
  create: (data) => api.post('/terms', data),
  update: (id, data) => api.put(`/terms/${id}`, data),
  delete: (id) => api.delete(`/terms/${id}`)
};

// Inventory API
export const inventoryAPI = {
  storageLocations: (location_id) => api.get('/inventory/storage-locations', { params: location_id ? { location_id } : {} }),
  // Categories
  getCategories: () => api.get('/inventory/categories'),
  createCategory: (data) => api.post('/inventory/categories', data),
  deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),
  
  // Items
  getItems: (params = {}) => api.get('/inventory/items', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  
  // Alerts
  getAlerts: () => api.get('/inventory/alerts'),

  // Import / Export
  downloadTemplate: () => api.get('/inventory/template', { responseType: 'blob' }),
  importItems: (file, options = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (options.dryRun) fd.append('dry_run', 'true');
    if (options.columnMap) fd.append('column_map', JSON.stringify(options.columnMap));
    return api.post('/inventory/import', fd, { headers: { 'Content-Type': undefined } });
  },
  previewImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/inventory/import/preview', fd, { headers: { 'Content-Type': undefined } });
  },
  exportItems: (format = 'xlsx') => api.get('/inventory/export', { params: { format }, responseType: 'blob' })
};

// Material Kits API (Solution Kits)
// Iter 55 — ad-hoc quotation lines → inventory promotion
export const adhocAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/adhoc-lines`),
  promote: (projectId, data) => api.post(`/projects/${projectId}/adhoc-lines/promote`, data),
  addToKit: (projectId, lineId, data) => api.post(`/projects/${projectId}/adhoc-lines/${lineId}/add-to-kit`, data)
};

export const materialKitsAPI = {
  getAll: (params = {}) => api.get('/material-kits', { params }),
  getOne: (id) => api.get(`/material-kits/${id}`),
  match: (system_type, capacity_kw) => api.get('/material-kits/match', { params: { system_type, capacity_kw } }),
  create: (data) => api.post('/material-kits', data),
  update: (id, data) => api.put(`/material-kits/${id}`, data),
  remove: (id) => api.delete(`/material-kits/${id}`)
};

// Solar Calculator API (server-side calculation engine + PIN/DISCOM lookup)
export const calcAPI = {
  lookupPincode: (pin) => api.get(`/calculate/lookup/${pin}`),
  solution: (payload) => api.post('/calculate/solution', payload),
  billSavings: (payload) => api.post('/calculate/bill-savings', payload),
  getConfig: () => api.get('/calculate/config'),
  updateConfig: (payload) => api.put('/calculate/config', payload),
  listDiscoms: (params = {}) => api.get('/calculate/discoms', { params }),
  getDiscom: (id) => api.get(`/calculate/discoms/${id}`),
  listPincodes: (params = {}) => api.get('/calculate/pincodes', { params }),
  seedDefaults: () => api.post('/calculate/seed-defaults'),
  validatePumpStringVoltage: (payload) => api.post('/calculate/pump/string-voltage', payload)
};

// Direct Sales API (Iter 39 Change 1)
export const salesAPI = {
  list: (params = {}) => api.get('/sales', { params }),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  edit: (id, data) => api.put(`/sales/${id}/edit`, data),
  remove: (id) => api.delete(`/sales/${id}`),
  addPayment: (id, data) => api.post(`/sales/${id}/payment`, data),
  return: (id, data) => api.post(`/sales/${id}/return`, data),
  invoice: (id) => api.get(`/sales/${id}/invoice`),
  summary: (params = {}) => api.get('/sales/summary', { params })
};

// Subsidy tracking + analytics (Iter 39 Change 2c)
export const subsidyAPI = {
  get: (project_id) => api.get(`/subsidy/tracking/${project_id}`),
  upsert: (data) => api.post('/subsidy/tracking', data),
  analytics: () => api.get('/subsidy/analytics')
};

// Marketing + CAC (Iter 39 Change 3)
export const marketingAPI = {
  summary: (params = {}) => api.get('/accounts/marketing-summary', { params }),
  cac: (params = {}) => api.get('/reports/cac', { params })
};
export const healthAPI = {
  getConfig: () => api.get('/dashboard/health/config'),
  updateConfig: (payload) => api.put('/dashboard/health/config', payload),
  snapshot: () => api.post('/dashboard/health/snapshot'),
  getHistory: (months = 12) => api.get('/dashboard/health/history', { params: { months } })
};

// Expansion Module API
export const expansionAPI = {
  overview: (params = {}) => api.get('/expansion/overview', { params }),
  district: (name) => api.get(`/expansion/district/${name}`),
  simulate: (payload) => api.post('/expansion/simulate', payload),
  getConfig: () => api.get('/expansion/config'),
  updateConfig: (payload) => api.put('/expansion/config', payload),
  listBranches: () => api.get('/expansion/branches'),
  createBranch: (payload) => api.post('/expansion/branches', payload),
  updateBranch: (id, payload) => api.put(`/expansion/branches/${id}`, payload),
  deleteBranch: (id) => api.delete(`/expansion/branches/${id}`)
};

// File Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, { headers: { 'Content-Type': undefined } });
  },
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/document', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 });
  },
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/media', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 });
  },
  uploadSiteImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/site-image', formData, { headers: { 'Content-Type': undefined }, timeout: 120000 });
  },
  getFileUrl: (path) => `${API_URL}/api/files/${path}`
};

// Site photo checklist (New project → Site photos, project page). Copied to Google Drive by the server.
export const sitePhotosAPI = {
  checklist: () => api.get('/site-photos/checklist'),
  /** meta = { lat, lng, accuracy, taken_at }; projectId attaches it straight to a saved project. */
  upload: (file, slot, meta = {}, projectId = null, onProgress = null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('slot', slot);
    Object.entries(meta).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') fd.append(k, String(v)); });
    if (projectId) fd.append('project_id', projectId);
    return api.post('/site-photos/upload', fd, {
      headers: { 'Content-Type': undefined }, timeout: 180000,
      onUploadProgress: onProgress ? (e) => e.total && onProgress(Math.round((e.loaded / e.total) * 100)) : undefined,
    });
  },
  list: (projectId) => api.get(`/projects/${projectId}/site-photos`),
  remove: (projectId, photoId) => api.delete(`/projects/${projectId}/site-photos/${photoId}`),
  sync: (projectId) => api.post(`/projects/${projectId}/site-photos/sync`),
};

// Google Drive connection (admin) — status is readable by everyone so forms can say where photos go.
export const driveAPI = {
  status: () => api.get('/integrations/google-drive'),
  connect: () => api.post('/integrations/google-drive/connect'),
  callback: (body) => api.post('/integrations/google-drive/callback', body),
  disconnect: () => api.delete('/integrations/google-drive'),
  syncAll: () => api.post('/integrations/google-drive/sync'),
  saveSecret: (client_secret) => api.put('/integrations/google-drive/client-secret', { client_secret }),
  deleteSecret: () => api.delete('/integrations/google-drive/client-secret'),
};

// Site GPS for a saved project (one tap on the project page)
export const siteGeoAPI = {
  pin: (projectId, { lat, lng, accuracy }) => api.put(`/projects/${projectId}/geo`, { lat, lng, accuracy }),
};

// Margin API
export const marginAPI = {
  update: (projectId, itemMargins) => api.put(`/projects/${projectId}/margin`, { item_margins: itemMargins })
};

// Deletion Requests API
export const deletionRequestsAPI = {
  getAll: (status = null) => api.get('/deletion-requests', { params: status ? { status } : {} }),
  approve: (id) => api.post(`/deletion-requests/${id}/approve`),
  reject: (id) => api.post(`/deletion-requests/${id}/reject`)
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: (params = {}) => api.get('/audit-logs', { params }),
  archives: () => api.get('/audit-logs/archives'),
  getArchiveConfig: () => api.get('/audit-logs/archive-config'),
  updateArchiveConfig: (data) => api.put('/audit-logs/archive-config', data),
  runArchive: (quarter) => api.post('/audit-logs/archives/run', null, { params: { quarter } }),
  purgeQuarter: (quarter) => api.post(`/audit-logs/archives/${quarter}/purge`)
};

// Company Profile API
export const companyAPI = {
  getAll: () => api.get('/company'),
  getActive: () => api.get('/company/active'),
  salesStats: () => api.get('/company/sales-stats'),
  create: (data) => api.post('/company', data),
  update: (id, data) => api.put(`/company/${id}`, data),
  delete: (id) => api.delete(`/company/${id}`),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/upload-logo', formData, {
      headers: { 'Content-Type': undefined }
    });
  }
};

// Approvals API
export const approvalsAPI = {
  getAll: (params = {}) => api.get('/approvals', { params }),
  getPendingCount: () => api.get('/approvals/pending-count'),
  create: (data) => api.post('/approvals', data),
  approve: (id) => api.put(`/approvals/${id}/approve`),
  reject: (id, reason) => api.put(`/approvals/${id}/reject`, { reason })
};

// Permissions API
export const permissionsAPI = {
  catalog: () => api.get('/permissions/catalog'),
  me: () => api.get('/permissions/me'),
  getAll: () => api.get('/permissions'),
  getRole: (role) => api.get(`/permissions/${role}`),
  updateRole: (role, permissions) => api.put(`/permissions/${role}`, { permissions })
};

// Form Tabs API (Dynamic Form Engine)
export const formTabsAPI = {
  getAll: () => api.get('/form-tabs'),
  create: (data) => api.post('/form-tabs', data),
  update: (id, data) => api.put(`/form-tabs/${id}`, data),
  delete: (id) => api.delete(`/form-tabs/${id}`),
  reorder: (order) => api.put('/form-tabs/reorder', { order })
};

// Accounts API (Cash on Hand, Meter Readings, Account Balance) — used by Credits page sub-tab + CEO Dashboard
export const accountsAPI = {
  list: (params = {}) => api.get('/accounts', { params }),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
  summary: () => api.get('/accounts/summary')
};

// Readings API (Site reading-phase tracker)
export const readingsAPI = {
  list: (params = {}) => api.get('/readings', { params }),
  summary: () => api.get('/readings/summary'),
  create: (data) => api.post('/readings', data),
  update: (id, data) => api.put(`/readings/${id}`, data),
  delete: (id) => api.delete(`/readings/${id}`),
  logGeneration: (id, data) => api.post(`/readings/${id}/generation`, data)
};

// Solar Report API (TNEB fetch + sizing calc + PDF merge)
export const solarReportAPI = {
  fetchTneb: (service_number, phone) => api.post('/tneb/fetch', { service_number, phone }),
  irradiation: (lat, lng) => api.get('/solar/irradiation', { params: { lat, lng } }),
  sizing: (data) => api.post('/solar/sizing', data),
  mergePdf: (generatedBlob, uploadedFile, position = 'prepend') => {
    const fd = new FormData();
    fd.append('generated_pdf', generatedBlob, 'sensoper_report.pdf');
    fd.append('uploaded_pdf', uploadedFile);
    fd.append('position', position);
    return api.post('/solar/merge-pdf', fd, {
      headers: { 'Content-Type': undefined },
      responseType: 'blob'
    });
  }
};

// Excess Material Reconciliation (Iter 42 Change 4)
export const reconciliationAPI = {
  get: (projectId) => api.get(`/material-reconciliation/${projectId}`),
  submit: (projectId, data) => api.put(`/material-reconciliation/${projectId}`, data),
  report: (params = {}) => api.get('/material-reconciliation-report', { params }),
  alerts: (days = 7) => api.get('/material-reconciliation-alerts', { params: { days } })
};

// Assets & Tools (Iter 42 Change 6)
export const assetsAPI = {
  list: (params = {}) => api.get('/assets', { params }),
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  remove: (id) => api.delete(`/assets/${id}`),
  issue: (id, data) => api.post(`/assets/${id}/issue`, data),
  returnAsset: (id, data) => api.post(`/assets/${id}/return`, data),
  logMaintenance: (id, data) => api.post(`/assets/${id}/maintenance`, data),
  compliance: (days = 90) => api.get('/assets/compliance', { params: { days } }),
  categories: () => api.get('/assets/categories'),
  filters: () => api.get('/assets/filters'),
  report: (type, params = {}) => api.get(`/assets/reports/${type}`, { params }),
  documents: (id) => api.get(`/assets/${id}/documents`),
  uploadDocument: (id, file, docType = 'other', notes = '', onProgress) => {
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', docType); fd.append('notes', notes);
    return api.post(`/assets/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: onProgress });
  },
  deleteDocument: (id, docId) => api.delete(`/assets/${id}/documents/${docId}`)
};

// Internal Teams (Iter 52)
export const teamsAPI = {
  list: (params = {}) => api.get('/internal-teams', { params }),
  get: (id) => api.get(`/internal-teams/${id}`),
  create: (data) => api.post('/internal-teams', data),
  update: (id, data) => api.put(`/internal-teams/${id}`, data),
  remove: (id) => api.delete(`/internal-teams/${id}`),
  performance: () => api.get('/internal-teams/performance'),
  projectTeams: (projectId) => api.get(`/projects/${projectId}/teams`),
  setProjectTeams: (projectId, teamIds, notes = '') => api.put(`/projects/${projectId}/teams`, { team_ids: teamIds, notes })
};

// AMC Contracts (Iter 42 Change 5)
export const amcAPI = {
  list: (params = {}) => api.get('/amc/contracts', { params }),
  get: (id) => api.get(`/amc/contracts/${id}`),
  create: (data) => api.post('/amc/contracts', data),
  createFromProject: (projectId) => api.post(`/amc/contracts/from-project/${projectId}`),
  update: (id, data) => api.put(`/amc/contracts/${id}`, data),
  renew: (id) => api.post(`/amc/contracts/${id}/renew`),
  bulkRenew: (ids) => api.post('/amc/contracts/bulk-renew', { contract_ids: ids }),
  cancel: (id, reason) => api.post(`/amc/contracts/${id}/cancel`, { reason }),
  hardDelete: (id, confirm) => api.delete(`/amc/contracts/${id}`, { params: { confirm } }),
  scheduleVisit: (id, data) => api.post(`/amc/contracts/${id}/visits`, data),
  completeVisit: (id, data) => api.put(`/amc/visits/${id}/complete`, data),
  listVisits: (params = {}) => api.get('/amc/visits', { params }),
  dashboard: (params = {}) => api.get('/amc/dashboard', { params }),
  recurringRevenueReport: (params = {}) => api.get('/amc/recurring-revenue-report', { params }),
  setInterest: (projectId, data) => api.put(`/amc/interest/${projectId}`, data),
  followUps: () => api.get('/amc/follow-ups'),
  scheduleWithNotifications: (id, data) => api.post(`/amc/contracts/${id}/schedule`, data),
  schedule: (params = {}) => api.get('/amc/schedule', { params }),
  batching: (days = 30) => api.get('/amc/batching', { params: { days } }),
  outbox: (allPending = false) => api.get('/amc/outbox', { params: { all_pending: allPending } }),
  outboxSent: (id) => api.post(`/amc/outbox/${id}/sent`),
  // offers, quotes, report, the AMC company
  settings: () => api.get('/amc/settings'),
  saveSettings: (data) => api.put('/amc/settings', data),
  offers: (params = {}) => api.get('/amc/offers', { params }),
  offersCount: () => api.get('/amc/offers/count'),
  flagOffer: (sourceType, sourceId, action, reason) => api.post(`/amc/offers/${sourceType}/${sourceId}`, { action, reason }),
  quotePreview: (data) => api.post('/amc/quotes/preview', data),
  quoteTerms: () => api.get('/amc/quotes/terms'),
  brand: () => api.get('/amc/brand'),
  quotes: (params = {}) => api.get('/amc/quotes', { params }),
  quote: (id) => api.get(`/amc/quotes/${id}`),
  createQuote: (data) => api.post('/amc/quotes', data),
  quoteStatus: (id, status, reason) => api.put(`/amc/quotes/${id}/status`, { status, reason }),
  acceptQuote: (id, data) => api.post(`/amc/quotes/${id}/accept`, data),
  report: (params = {}) => api.get('/amc/report', { params })
};
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`)
};

// Multi-location (Iter 42 Change 8)
export const locationsAPI = {
  list: () => api.get('/locations'),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  remove: (id) => api.delete(`/locations/${id}`),
  assignUser: (userId, data) => api.put(`/users/${userId}/locations`, data)
};

// Vendors / Suppliers (Iter 44 Batch C)
export const vendorsAPI = {
  list: (params = {}) => api.get('/vendors', { params }),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  remove: (id) => api.delete(`/vendors/${id}`),
  purchaseOrders: (id) => api.get(`/vendors/${id}/purchase-orders`),
  tags: () => api.get('/vendors/tags'),
  scorecard: (id) => api.get(`/vendors/${id}/scorecard`),
  rate: (id, data) => api.post(`/vendors/${id}/ratings`, data),
  removeRating: (id, ratingId) => api.delete(`/vendors/${id}/ratings/${ratingId}`),
  priceHistory: (params) => api.get('/vendors/price-history', { params }),
};

// AI agents (Settings → AI agents): keys, connectors, activity — and the approval page for Claude / ChatGPT connectors
export const agentsAPI = {
  settings: () => api.get('/agents/settings'),
  saveSettings: (data) => api.put('/agents/settings', data),
  list: () => api.get('/agents'),
  users: () => api.get('/agents/users'),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  rotate: (id) => api.post(`/agents/${id}/rotate`),
  remove: (id) => api.delete(`/agents/${id}`),
  activity: (params = {}) => api.get('/agents/activity', { params }),
  request: (id) => api.get(`/oauth/requests/${id}`),
  approve: (id, data) => api.post(`/oauth/requests/${id}/approve`, data),
  deny: (id) => api.post(`/oauth/requests/${id}/deny`),
};

// Brands & trademarks (own brands, dealership / distribution brands)
export const brandsAPI = {
  list: (params = {}) => api.get('/brands', { params }),
  get: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  remove: (id) => api.delete(`/brands/${id}`),
  report: (params = {}) => api.get('/brands/report', { params }),
  trademarks: () => api.get('/brands/trademarks'),
  addTrademark: (brandId, data) => api.post(`/brands/${brandId}/trademarks`, data),
  updateTrademark: (id, data) => api.put(`/brands/trademarks/${id}`, data),
  removeTrademark: (id) => api.delete(`/brands/trademarks/${id}`),
  suggestItems: (id) => api.get(`/brands/${id}/suggest-items`),
  linkItems: (id, itemIds) => api.post(`/brands/${id}/items`, { item_ids: itemIds }),
  unlinkItem: (id, itemId) => api.delete(`/brands/${id}/items/${itemId}`),
};

// Purchase requests: ask vendors for quotes, compare, choose → PO
export const procurementAPI = {
  list: (params = {}) => api.get('/purchase-requests', { params }),
  get: (id) => api.get(`/purchase-requests/${id}`),
  create: (data) => api.post('/purchase-requests', data),
  update: (id, data) => api.put(`/purchase-requests/${id}`, data),
  addQuote: (id, data) => api.post(`/purchase-requests/${id}/quotes`, data),
  editQuote: (id, quoteId, data) => api.put(`/purchase-requests/${id}/quotes/${quoteId}`, data),
  removeQuote: (id, quoteId) => api.delete(`/purchase-requests/${id}/quotes/${quoteId}`),
  select: (id, data) => api.post(`/purchase-requests/${id}/select`, data),
  cancel: (id, reason) => api.post(`/purchase-requests/${id}/cancel`, { reason }),
  savings: (params = {}) => api.get('/purchase-requests/savings', { params }),
  settings: () => api.get('/procurement/settings'),
  saveSettings: (data) => api.put('/procurement/settings', data),
};

// HR: people, hiring, cover, exits, shifts
export const hrAPI = {
  overview: () => api.get('/hr/overview'),
  settings: () => api.get('/hr/settings'),
  saveSettings: (data) => api.put('/hr/settings', data),
  people: (params = {}) => api.get('/hr/people', { params }),
  updatePerson: (id, data) => api.put(`/hr/people/${id}`, data),
  openings: () => api.get('/hr/openings'),
  addOpening: (data) => api.post('/hr/openings', data),
  updateOpening: (id, data) => api.put(`/hr/openings/${id}`, data),
  candidates: (params = {}) => api.get('/hr/candidates', { params }),
  addCandidate: (data) => api.post('/hr/candidates', data),
  updateCandidate: (id, data) => api.put(`/hr/candidates/${id}`, data),
  moveStage: (id, stage, note) => api.post(`/hr/candidates/${id}/stage`, { stage, note }),
  addInterview: (id, data) => api.post(`/hr/candidates/${id}/interviews`, data),
  interviewResult: (id, ivId, data) => api.put(`/hr/candidates/${id}/interviews/${ivId}`, data),
  hire: (id, data) => api.post(`/hr/candidates/${id}/hire`, data),
  covers: (params = {}) => api.get('/hr/covers', { params }),
  suggestCover: (params) => api.get('/hr/covers/suggest', { params }),
  addCover: (data) => api.post('/hr/covers', data),
  updateCover: (id, data) => api.put(`/hr/covers/${id}`, data),
  cancelCover: (id) => api.post(`/hr/covers/${id}/cancel`),
  exits: (params = {}) => api.get('/hr/exits', { params }),
  addExit: (data) => api.post('/hr/exits', data),
  updateExit: (id, data) => api.put(`/hr/exits/${id}`, data),
  withdrawExit: (id) => api.post(`/hr/exits/${id}/withdraw`),
  completeExit: (id) => api.post(`/hr/exits/${id}/complete`),
  shifts: () => api.get('/hr/shifts'),
  addShift: (data) => api.post('/hr/shifts', data),
  updateShift: (id, data) => api.put(`/hr/shifts/${id}`, data),
  roster: (week) => api.get('/hr/roster', { params: { week } }),
  saveRoster: (entries) => api.put('/hr/roster', { entries }),
  copyWeek: (fromWeek, toWeek) => api.post('/hr/roster/copy', { from_week: fromWeek, to_week: toWeek }),
};

// Company loans
export const loansAPI = {
  list: (params = {}) => api.get('/loans', { params }),
  get: (id) => api.get(`/loans/${id}`),
  create: (data) => api.post('/loans', data),
  update: (id, data) => api.put(`/loans/${id}`, data),
  remove: (id) => api.delete(`/loans/${id}`),
  emiPreview: (data) => api.post('/loans/emi-preview', data),
  addPayment: (id, data) => api.post(`/loans/${id}/payments`, data),
  removePayment: (id, paymentId) => api.delete(`/loans/${id}/payments/${paymentId}`),
  close: (id, data) => api.post(`/loans/${id}/close`, data),
};

// Event calendar
export const calendarAPI = {
  range: (start, end, sources) => api.get('/calendar', { params: { start, end, ...(sources ? { sources } : {}) } }),
  people: () => api.get('/calendar/people'),
  add: (data) => api.post('/calendar/events', data),
  update: (id, data) => api.put(`/calendar/events/${id}`, data),
  remove: (id) => api.delete(`/calendar/events/${id}`),
};

// Employee Performance manual scores (Iter 44 Batch C)
export const employeeScoresAPI = {
  list: (params = {}) => api.get('/employee-scores', { params }),
  create: (data) => api.post('/employee-scores', data)
};

// Labour & Subcontractor / Internal Teams (Iter 46 Change 1)
export const partnersAPI = {
  list: (params = {}) => api.get('/partners', { params }),
  districts: () => api.get('/partners/meta/districts'),
  create: (data) => api.post('/partners', data),
  get: (id) => api.get(`/partners/${id}`),
  update: (id, data) => api.put(`/partners/${id}`, data),
  addRateCard: (id, data) => api.post(`/partners/${id}/rate-card`, data),
  editRateCard: (id, data) => api.put(`/partners/${id}/rate-card`, data),
  remove: (id, reason) => api.delete(`/partners/${id}`, { data: { reason } }),
  projectScope: (projectId) => api.get(`/partners/project-scope/${projectId}`),
  assignmentsByProject: (projectId) => api.get(`/partners/assignments/by-project/${projectId}`),
  createAssignment: (partnerId, data) => api.post(`/partners/${partnerId}/assignments`, data),
  getAssignment: (id) => api.get(`/partners/assignments/${id}`),
  updateAssignment: (id, data) => api.put(`/partners/assignments/${id}`, data),
  releaseRetention: (id) => api.post(`/partners/assignments/${id}/release-retention`),
  recordPayment: (partnerId, data) => api.post(`/partners/${partnerId}/payments`, data),
  listPayments: (partnerId, params = {}) => api.get(`/partners/${partnerId}/payments`, { params }),
  scorecard: (partnerId) => api.get(`/partners/${partnerId}/scorecard`),
  tags: {
    list: () => api.get('/partners/tags/all'),
    create: (tag) => api.post('/partners/tags', { tag }),
    rename: (id, tag) => api.put(`/partners/tags/${id}`, { tag }),
    remove: (id) => api.delete(`/partners/tags/${id}`)
  }
};

// Ecommerce marketplaces (Iter 46 Change 2)
export const ecommerceAPI = {
  platforms: {
    list: () => api.get('/ecommerce/platforms'),
    create: (data) => api.post('/ecommerce/platforms', data),
    update: (id, data) => api.put(`/ecommerce/platforms/${id}`, data),
    remove: (id) => api.delete(`/ecommerce/platforms/${id}`)
  },
  listings: {
    list: (params = {}) => api.get('/ecommerce/listings', { params }),
    create: (data) => api.post('/ecommerce/listings', data),
    update: (id, data) => api.put(`/ecommerce/listings/${id}`, data),
    bulkStatus: (data) => api.post('/ecommerce/listings/bulk-status', data),
    remove: (id) => api.delete(`/ecommerce/listings/${id}`)
  },
  orders: {
    list: (params = {}) => api.get('/ecommerce/orders', { params }),
    create: (data) => api.post('/ecommerce/orders', data),
    update: (id, data) => api.put(`/ecommerce/orders/${id}`, data),
    remove: (id) => api.delete(`/ecommerce/orders/${id}`),
    importPreview: (platformId, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/ecommerce/orders/import-preview?platform_id=${platformId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    importCommit: (data) => api.post('/ecommerce/orders/import-commit', data)
  },
  reconciliation: (params = {}) => api.get('/ecommerce/reconciliation', { params }),
  products: {
    list: (params = {}) => api.get('/ecommerce/products', { params }),
    create: (data) => api.post('/ecommerce/products', data),
    upsertListing: (itemId, platformId, data) => api.put(`/ecommerce/products/${itemId}/platforms/${platformId}`, data),
    delist: (itemId) => api.delete(`/ecommerce/products/${itemId}`)
  }
};

// Customer Support (Iter 47) — AMC support tickets
export const supportAPI = {
  slaConfig: {
    get: () => api.get('/support/sla-config'),
    update: (data) => api.put('/support/sla-config', data)
  },
  tickets: {
    list: (params = {}) => api.get('/support/tickets', { params }),
    get: (id) => api.get(`/support/tickets/${id}`),
    create: (data) => api.post('/support/tickets', data),
    update: (id, data) => api.put(`/support/tickets/${id}`, data),
    transitionStatus: (id, data) => api.post(`/support/tickets/${id}/status`, data),
    close: (id, data) => api.post(`/support/tickets/${id}/close`, data)
  },
  dashboard: () => api.get('/support/dashboard')
};

// Hard delete (Iter 47) — admin only
export const hardDeleteAPI = {
  sale: (id, data) => api.delete(`/hard-delete/sale/${id}`, { data }),
  purchaseOrder: (id, data) => api.delete(`/hard-delete/purchase-order/${id}`, { data }),
  delivery: (id, data) => api.delete(`/hard-delete/delivery/${id}`, { data })
};

export default api;
// Attendance — check in / out with time + GPS
export const attendanceAPI = {
  today: () => api.get('/attendance/me/today'),
  checkIn: (body) => api.post('/attendance/check-in', body),
  checkOut: (body) => api.post('/attendance/check-out', body),
  myMonth: (month) => api.get('/attendance/me', { params: { month } }),
  team: (date) => api.get('/attendance/team', { params: { date } }),
  register: (month) => api.get('/attendance/register', { params: { month } }),
  correct: (id, body) => api.put(`/attendance/${id}`, body),
};

// Location-wise organisation structure (admins)
export const orgAPI = {
  structure: () => api.get('/org-structure'),
};

// Customer dashboard — staff side (share link per project, offers)
export const customerDashboardAPI = {
  getLink: (projectId) => api.get(`/projects/${projectId}/portal-link`),
  createLink: (projectId) => api.post(`/projects/${projectId}/portal-link`),
  revokeLink: (projectId) => api.delete(`/projects/${projectId}/portal-link`),
  offers: () => api.get('/customer-offers'),
  createOffer: (body) => api.post('/customer-offers', body),
  updateOffer: (id, body) => api.put(`/customer-offers/${id}`, body),
  deleteOffer: (id) => api.delete(`/customer-offers/${id}`),
  interests: () => api.get('/customer-offers/interests'),
  markInterest: (id) => api.put(`/customer-offers/interests/${id}`),
};

// Customer dashboard — the customer's own page (no staff login; session cookie scoped to the link)
const portalHttp = axios.create({ baseURL: `${API_URL}/api/portal`, withCredentials: true, headers: { 'Content-Type': 'application/json' } });
export const portalAPI = {
  hello: (token) => portalHttp.get(`/${token}/hello`),
  verify: (token, phone) => portalHttp.post(`/${token}/verify`, { phone }),
  dashboard: (token) => portalHttp.get(`/${token}/dashboard`),
  raiseTicket: (token, body) => portalHttp.post(`/${token}/tickets`, body),
  interested: (token, offerId) => portalHttp.post(`/${token}/offers/${offerId}/interest`),
  logout: (token) => portalHttp.post(`/${token}/logout`),
};

// Investors — admin side (staff session, admins only)
export const investorsAPI = {
  meta: () => api.get('/investors/meta'),
  overview: () => api.get('/investors/overview'),
  business: (months = 12) => api.get('/investors/business', { params: { months } }),
  list: () => api.get('/investors'),
  create: (body) => api.post('/investors', body),
  update: (id, body) => api.put(`/investors/${id}`, body),
  remove: (id) => api.delete(`/investors/${id}`),
  ledger: (id) => api.get(`/investors/${id}/ledger`),
  addEntry: (id, body) => api.post(`/investors/${id}/ledger`, body),
  removeEntry: (id, entryId) => api.delete(`/investors/${id}/ledger/${entryId}`),
  preview: (id, months = 12) => api.get(`/investors/${id}/preview`, { params: { months } }),
  activity: (id) => api.get(`/investors/${id}/activity`),
};

// Investor's own session — a separate cookie that only works under /api/investor
const investorHttp = axios.create({ baseURL: `${API_URL}/api/investor`, withCredentials: true, headers: { 'Content-Type': 'application/json' } });
export const investorPortalAPI = {
  login: (email, password) => investorHttp.post('/auth/login', { email, password }),
  logout: () => investorHttp.post('/auth/logout'),
  me: () => investorHttp.get('/me'),
  changePassword: (current_password, new_password) => investorHttp.post('/auth/change-password', { current_password, new_password }),
  dashboard: (months = 12) => investorHttp.get('/dashboard', { params: { months } }),
};
